import { Check, X, ChevronDown, Phone, MessageSquare, Star, FileText, TrendingUp, GitBranch, Monitor, Mail, Target, Sparkles, HelpCircle, Tag, Play } from "lucide-react";
import AnimatedBorderBadge from "@/components/editorial/AnimatedBorderBadge";
import EditorialHeader from "@/components/editorial/EditorialHeader";
import EditorialFooter from "@/components/editorial/EditorialFooter";
import EditorialGridLayout from "@/components/editorial/EditorialGridLayout";
import GridLinesWrapper from "@/components/editorial/GridLinesWrapper";
import EditorialSectionDivider from "@/components/editorial/EditorialSectionDivider";
import { useTheme } from "@/contexts/ThemeContext";
import CalendarPopup from "@/components/CalendarPopup";
import { StructuredData } from "@/components/seo/StructuredData";
import { generateFAQSchema } from "@/utils/structuredData";
import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { usePageSEO } from "@/hooks/usePageSEO";
import MostPopularFeatures from "@/components/pricing/MostPopularFeatures";
import FeatureComparisonMatrix from "@/components/pricing/FeatureComparisonMatrix";
import AnimatedPricingMockup from "@/components/pricing/AnimatedPricingMockup";
import AnimatedAIMockup from "@/components/pricing/AnimatedAIMockup";

const PricingContent = () => {
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [isAnnual, setIsAnnual] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [showVideoModal, setShowVideoModal] = useState(false);

  // Theme-aware colors
  const bgColor = theme === 'dark' ? '#111111' : '#FCFCF9';
  const sectionBg = theme === 'dark' ? '#111111' : 'linear-gradient(to bottom, #FFFFFF 0%, #FCFCF9 100%)';
  const headingColor = theme === 'dark' ? '#FFFFFF' : '#111111';
  const textColor = theme === 'dark' ? '#AAAAAA' : '#555555';
  const cardBgColor = theme === 'dark' ? '#1A1A1A' : '#FFFFFF';
  const borderColor = theme === 'dark' ? '#333333' : '#E0E0E0';
  const iconColor = theme === 'dark' ? '#AAAAAA' : '#555555';
  const toggleBg = theme === 'dark' ? '#1A1A1A' : '#FFFFFF';
  const toggleBorder = theme === 'dark' ? '#333333' : '#E0E0E0';
  const featureCardBg = theme === 'dark' ? '#1A1A1A' : '#F8F8F8';
  const featureIconBg = theme === 'dark' ? '#111111' : '#FFFFFF';
  const secondaryTextColor = theme === 'dark' ? '#CCCCCC' : '#333333';
  const popularBorderColor = theme === 'dark' ? '#FFFFFF' : '#111111';
  const ctaBtnBg = theme === 'dark' ? '#FFFFFF' : '#111111';
  const ctaBtnText = theme === 'dark' ? '#111111' : '#FFFFFF';
  const secondaryBtnBg = theme === 'dark' ? '#1A1A1A' : '#FCFCF9';
  const secondaryBtnText = theme === 'dark' ? '#FFFFFF' : '#111111';

  // Handle scroll to pricing section when hash is present
  useEffect(() => {
    if (location.hash === '#pricing-tables' || location.hash === '#pricing-section') {
      const targetId = location.hash.substring(1);
      const scrollToPricing = () => {
        const element = document.getElementById(targetId);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
          
          setTimeout(() => {
            const headerHeight = window.innerWidth < 768 ? 100 : 120;
            const elementRect = element.getBoundingClientRect();
            if (elementRect.top < headerHeight) {
              window.scrollBy({
                top: elementRect.top - headerHeight,
                behavior: 'smooth'
              });
            }
          }, 600);
        }
      };

      if (location.hash) {
        setTimeout(scrollToPricing, 0);
        setTimeout(scrollToPricing, 100);
        setTimeout(scrollToPricing, 300);
      }
    }
  }, [location.hash]);

  const calculateDiscount = (monthly: number, annual: number) => {
    const monthlyTotal = monthly * 12;
    if (annual >= monthlyTotal) return 0;
    return Math.round(((monthlyTotal - annual) / monthlyTotal) * 100);
  };

  const plans = [
    {
      name: "Minimums",
      subtitle: "The essential infrastructure",
      monthlyPrice: 197,
      annualPrice: 1970,
      description: "Marketing Platform Foundation",
      features: [
        "Unlimited User Seats",
        "3 Client Sub-Accounts",
        "$10 complimentary credits each month (approx. 10,000 emails or 770 texts)",
        "Website & Funnel Builder",
        "CRM & Pipelines",
        "Email + SMS Marketing",
        "Unified Conversations",
        "Social Media Management",
        "Workflow Automation",
        "Forms, Surveys & Quizzes",
        "Calendar & Bookings",
        "Ad Manager",
        "Payments & Invoicing",
        "Web Chat Widget",
        "Missed Call Text Back",
        "24/7 Support",
        "Basic API Access",
        "1:1 Onboarding",
        "10 weeks of Group Training",
        "Demo Video Library"
      ],
      cta: "Start Free Trial",
      monthlyUrl: "https://buy.stripe.com/5kQ14o3HV0yO332bjta3u09",
      annualUrl: "https://buy.stripe.com/cNiaEYfqDdlA7jibjta3u0a",
      popular: false
    },
    {
      name: "Professional",
      subtitle: "The professional business benchmark",
      monthlyPrice: 297,
      annualPrice: 2970,
      description: "Advanced Automation & Multi-Channel Growth",
      features: [
        "All Minimums features",
        "Native AI Tool for Text Conversations, Phone Calls, Content, Web page Building and Review Management",
        "Booking Calendars",
        "Online Reputation Management",
        "Communities",
        "Courses",
        "Grey Labeled Mobile App",
        "Google Business Profile Optimisation",
        "Google Business Profile Call Tracking",
        "Facebook Messenger integration",
        "Text to Pay",
        "Unlimited User Seats",
        "Unlimited Client Sub-Accounts",
        "$25 complimentary credits each month (approx. 25,000 emails)",
        "Advanced Workflow Automation",
        "Advanced Triggers & Webhooks",
        "Custom Integrations (Zapier, Make, API)",
        "Advanced Email & SMS Campaigns",
        "A/B Testing",
        "Advanced Reporting & Analytics",
        "Multi-channel Attribution",
        "Campaign Performance Tracking",
        "Lead Scoring & Segmentation",
        "Custom Fields & Tags",
        "Bulk Actions & Imports",
        "DFY A2P registration",
        "Priority Support & Unlimited Zoom",
        "Dedicated Success Manager"
      ],
      cta: "Start Free Trial",
      monthlyUrl: "https://buy.stripe.com/eVqcN62DRgxMbzy2MXa3u0b",
      annualUrl: "https://buy.stripe.com/5kQ9AU3HV0yO6fe1ITa3u0c",
      popular: true
    },
    {
      name: "Enterprise",
      subtitle: "The elite, AI-driven executive suite",
      monthlyPrice: 697,
      annualPrice: 6970,
      description: "Enterprise-Grade Platform with SaaS Mode & Advanced AI",
      features: [
        "All Minimums & Professional features",
        "Unlimited User Seats",
        "Unlimited Client Sub-Accounts",
        "$100 complimentary credits each month (approx. 100,000 emails)",
        "Unused credits rollover",
        "SaaS Mode (White-label & Resell Platform)",
        "Rebill Phone & Email with Markup",
        "Advanced API Access",
        "Automated Account Reporting",
        "Advanced AI Workflow Builder",
        "Advanced AI Funnel Builder",
        "Advanced AI Website Builder",
        "AI Content Generation (Blogs, Social, Emails)",
        "E-Signing, Documents & Contracts",
        "Advanced Invoicing & Payments (Stripe integration)",
        "Advanced Memberships & Courses Platform",
        "Advanced Social Planner with AI Scheduling",
        "Advanced Reporting (Custom Dashboards, Attribution)",
        "AI Chat with Custom Training",
        "AI Convo (Voice AI)",
        "Outbound Sales AI & Dialer",
        "Advanced AI Access & Priority Processing",
        "Custom AI Knowledge Base Setup",
        "Custom Branding & White-labeling",
        "20+ Premium Funnel Templates",
        "70+ Premium Snapshots",
        "Private Slack Channel",
        "Dedicated Account Manager",
        "Lifetime Pricing Guarantee"
      ],
      cta: "Start Free Trial",
      monthlyUrl: "https://buy.stripe.com/5kQ9AU3HV0yO6fe1ITa3u0c",
      annualUrl: "https://buy.stripe.com/aFa7sM7Yb0yO9rqfzJa3u0g",
      popular: false
    }
  ];

  const faqs = [
    {
      question: "What is the main difference between the SaaS plans and Done-For-You (DFY) Service?",
      answer: "SaaS plans give you access to our branded software, templates, and training—it's a DIY system you run. DFY Services include the software, plus our team actively manages and executes your marketing campaigns (ads, content, funnels, etc.) for you."
    },
    {
      question: "Is the DFY price a monthly retainer, and what does it include?",
      answer: "Yes, the DFY price is a fixed monthly retainer. It includes access to our highest-tier SaaS plan, plus a specific list of managed services, such as ad management, content creation, or full funnel optimization. Detailed service scope is confirmed via contract."
    },
    {
      question: "Can I start with a SaaS plan and upgrade to a DFY service later?",
      answer: "Absolutely. Many clients do this! You can upgrade at any time. We will credit the cost of your current SaaS subscription towards your first month of the DFY service retainer."
    },
    {
      question: "Do I have to pay per user or per client location?",
      answer: "No. All of our plans—SaaS and DFY—include unlimited user seats. You can add your entire internal team without incurring any extra per-user fees."
    },
    {
      question: "Are there any contracts or long-term commitments for the DFY service?",
      answer: "Our DFY services operate on a 90-day minimum agreement to ensure we have time to generate meaningful results. After the initial term, the service automatically moves to a flexible month-to-month basis."
    },
    {
      question: "What is the cost of the AI and communication tools (SMS/Email) in the SaaS plans?",
      answer: "All core AI features are included. Communication costs are pay-as-you-go, meaning we rebill the carrier costs (Twilio, Mailgun) with a small markup. This ensures you only pay pennies per message you actually send."
    },
    {
      question: "Is there a setup fee for the Done-For-You (DFY) services?",
      answer: "Yes, all DFY services require a one-time setup fee (clearly listed on the pricing table). This covers niche customization, full platform configuration, and initial funnel build-out by our team."
    },
    {
      question: "If I choose a SaaS plan, do I get any support?",
      answer: "Yes. All SaaS plans include dedicated chat and email support during business hours, access to our full knowledge base, and recorded onboarding videos. DFY clients receive priority support."
    },
    {
      question: "Can I get a free trial for the software before committing?",
      answer: "Yes. We offer a 14-day free trial for our highest-tier SaaS plan. This allows you to test the core features, load a template, and see the dashboard before entering any payment information."
    },
    {
      question: "Who owns the ads I run and the data generated through the platform?",
      answer: "You own all of it. All ad accounts remain in your business's name, and all client data (leads, contacts, funnels) is 100% your property, regardless of which plan you choose."
    }
  ];

  const faqSchemaData = faqs.map(faq => ({
    question: faq.question,
    answer: faq.answer
  }));

  return (
    <>
      <StructuredData data={generateFAQSchema(faqSchemaData)} />
      <EditorialGridLayout>
        <div className="min-h-screen overflow-x-hidden" style={{ backgroundColor: bgColor }}>
          {/* Header */}
          <EditorialHeader />

          {/* Hero Section */}
          <section className="pt-32 pb-16 px-6" style={{ background: sectionBg }}>
            <div className="max-w-4xl mx-auto text-center">
            <AnimatedBorderBadge text="Pricing" icon={Tag} />
            
            <h1 
              className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4"
              style={{ color: headingColor }}
            >
              Scale Your Business. Not Your Costs.
            </h1>
            
            <p 
              className="text-sm max-w-2xl mx-auto mb-8"
              style={{ color: textColor }}
            >
              Replace your scattered toolstack with one powerful platform. Save thousands yearly while getting more done.
            </p>

            {/* Animated Pricing Mockup */}
            <AnimatedPricingMockup />

            {/* Benefit chips */}
            <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
              {['14-day free trial', 'No setup fees', 'Cancel anytime', 'Free CRM migration'].map((benefit, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs cursor-pointer transition-all duration-200 hover:scale-105 active:scale-95"
                  style={{ 
                    backgroundColor: cardBgColor, 
                    border: `1px solid ${borderColor}`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = theme === 'dark' ? '#252525' : '#F5F5F5';
                    e.currentTarget.style.borderColor = theme === 'dark' ? '#444444' : '#CCCCCC';
                    e.currentTarget.style.boxShadow = theme === 'dark' 
                      ? '0 4px 12px rgba(255,255,255,0.05)' 
                      : '0 4px 12px rgba(0,0,0,0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = cardBgColor;
                    e.currentTarget.style.borderColor = borderColor;
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div 
                    className="w-4 h-4 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: ctaBtnBg }}
                  >
                    <Check className="w-2.5 h-2.5" style={{ color: ctaBtnText }} />
                  </div>
                  <span style={{ color: headingColor }}>{benefit}</span>
                </div>
              ))}
            </div>

            {/* Watch Demo Button */}
            <div className="mt-6">
              <button 
                onClick={() => setShowVideoModal(true)}
                className="px-4 py-2 text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2 rounded-lg hover:scale-[1.02] active:scale-[0.98] hover:shadow-sm mx-auto"
                style={{
                  backgroundColor: cardBgColor,
                  color: headingColor,
                  border: `1px solid ${borderColor}`
                }}
              >
                <Play className="w-3 h-3" fill="currentColor" />
                Watch Demo
              </button>
            </div>
          </div>
        </section>

        <EditorialSectionDivider />

        {/* Most Popular Features Section */}
        <GridLinesWrapper>
          <MostPopularFeatures />
        </GridLinesWrapper>

        <EditorialSectionDivider />

        {/* Pricing Section - V3 Style */}
        <GridLinesWrapper>
          <section id="pricing-section" className="py-16 scroll-mt-24">
            <div className="max-w-[960px] mx-auto px-6 lg:px-8">
              <AnimatedBorderBadge text="Pricing" icon={Tag} />

              {/* Section Header */}
              <div className="text-center mb-8">
                <h2 className="text-3xl lg:text-4xl font-bold mb-2" style={{ color: headingColor }}>
                  Choose Your Plan.
                </h2>
                <p className="text-sm" style={{ color: textColor }}>
                  Start for free.
                </p>
              </div>

              {/* Toggle with Save Badge */}
              <div id="pricing-tables" className="flex flex-col items-center mb-6 scroll-mt-32">
                <div 
                  className="inline-flex items-center rounded-full p-1"
                  style={{ backgroundColor: toggleBg, border: `1px solid ${toggleBorder}` }}
                >
                  <button
                    onClick={() => setIsAnnual(false)}
                    className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all duration-200`}
                    style={{
                      backgroundColor: !isAnnual ? ctaBtnBg : 'transparent',
                      color: !isAnnual ? ctaBtnText : textColor
                    }}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setIsAnnual(true)}
                    className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all duration-200`}
                    style={{
                      backgroundColor: isAnnual ? ctaBtnBg : 'transparent',
                      color: isAnnual ? ctaBtnText : textColor
                    }}
                  >
                    Annual
                  </button>
                </div>
                {/* Save Badge - below toggle */}
                <div className="mt-3">
                  <AnimatedBorderBadge text="Save up to 17%" />
                </div>
              </div>

              {/* Benefits */}
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                {['14-day free trial', 'No setup fees', 'Cancel anytime', 'Free CRM Migration', '1:1 Onboarding'].map((benefit, index) => (
                  <div key={index} className="flex items-center gap-1.5 text-xs" style={{ color: textColor }}>
                    <Check className="w-3 h-3" style={{ color: headingColor }} />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>

              {/* Pricing Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {plans.map((plan, index) => {
                  const displayPrice = isAnnual ? Math.round(plan.annualPrice / 12) : plan.monthlyPrice;
                  const url = isAnnual ? plan.annualUrl : plan.monthlyUrl;
                  
                  return (
                    <div
                      key={index}
                      className="relative rounded-2xl border overflow-hidden transition-all duration-200 hover:shadow-lg"
                      style={{
                        backgroundColor: cardBgColor,
                        borderColor: plan.popular ? popularBorderColor : borderColor,
                        boxShadow: plan.popular 
                          ? theme === 'dark' 
                            ? '0 10px 30px -10px rgba(255, 255, 255, 0.1)' 
                            : '0 10px 30px -10px rgba(0, 0, 0, 0.15)'
                          : undefined
                      }}
                    >
                      {/* Popular Badge */}
                      {plan.popular && (
                        <div 
                          className="absolute top-0 left-0 right-0 text-center py-1.5"
                          style={{ backgroundColor: ctaBtnBg, color: ctaBtnText }}
                        >
                          <span className="text-[10px] font-semibold uppercase tracking-wider">Most Popular</span>
                        </div>
                      )}

                      <div className={`p-5 ${plan.popular ? 'pt-12' : ''}`}>
                        {/* Plan Name & Price */}
                        <div className="mb-4">
                          <h3 className="text-lg font-bold mb-1" style={{ color: headingColor }}>{plan.name}</h3>
                          <div className="flex items-baseline gap-1">
                            <span className="text-3xl font-bold" style={{ color: headingColor }}>${displayPrice}</span>
                            <span className="text-xs" style={{ color: textColor }}>/month</span>
                          </div>
                          {isAnnual && (
                            <p className="text-[10px] mt-1" style={{ color: textColor }}>
                              Billed ${plan.annualPrice}/year
                            </p>
                          )}
                        </div>

                        {/* Description */}
                        <p 
                          className="text-xs mb-4 pb-4"
                          style={{ color: textColor, borderBottom: `1px solid ${borderColor}` }}
                        >
                          {plan.description}
                        </p>

                        {/* Features */}
                        <div className="mb-4">
                          <ul className="space-y-2">
                            {plan.features.map((feature, featureIndex) => (
                              <li key={featureIndex} className="flex items-start gap-2">
                                <div 
                                  className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                                  style={{ backgroundColor: ctaBtnBg }}
                                >
                                  <Check className="w-2.5 h-2.5" style={{ color: ctaBtnText }} strokeWidth={3} />
                                </div>
                                <span className="text-xs leading-relaxed" style={{ color: textColor }}>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* CTA Button */}
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block w-full text-center py-2 rounded-lg text-xs font-semibold transition-all duration-200 hover:scale-105"
                          style={{
                            backgroundColor: plan.popular ? ctaBtnBg : secondaryBtnBg,
                            color: plan.popular ? ctaBtnText : secondaryBtnText,
                            border: plan.popular ? 'none' : `1px solid ${borderColor}`
                          }}
                        >
                          Start Free Trial
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        </GridLinesWrapper>

        <EditorialSectionDivider />

        {/* Feature Comparison Matrix */}
        <FeatureComparisonMatrix />

        <EditorialSectionDivider />

        {/* AI Agents Section */}
        <GridLinesWrapper>
          <section className="py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div>
                <AnimatedBorderBadge text="AI Agents" icon={Sparkles} align="left" />

                <h2 
                  className="text-3xl lg:text-4xl font-bold tracking-tight mb-4"
                  style={{ color: headingColor }}
                >
                  A(bsolutely) I(ncredible) Agents.
                </h2>

                <p 
                  className="text-sm mb-8"
                  style={{ color: textColor }}
                >
                  Your AI Employees are on 24/7, never take PTO or get sick. No tax, no pensions, just results.
                </p>

                {/* AI Feature list */}
                <div className="space-y-3 mb-8">
                  {[
                    { icon: Phone, text: "Voice AI - 24/7 virtual sales agent" },
                    { icon: MessageSquare, text: "Conversation AI - instant engagement" },
                    { icon: Star, text: "Reviews AI - automated reputation" },
                    { icon: FileText, text: "Content AI - creative assistant" },
                    { icon: GitBranch, text: "Workflow AI - automation by description" },
                  ].map((item, index) => (
                    <div 
                      key={index}
                      className="flex items-center gap-3"
                    >
                      <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-emerald-600" />
                      </div>
                      <span className="text-sm" style={{ color: secondaryTextColor }}>{item.text}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-3">
                  <Link 
                    to="/ai"
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 hover:scale-[1.02] hover:shadow-md active:scale-[0.98]"
                    style={{ 
                      backgroundColor: secondaryBtnBg, 
                      color: secondaryBtnText,
                      border: `1px solid ${borderColor}`
                    }}
                  >
                    <Sparkles className="w-4 h-4" />
                    Learn More
                  </Link>
                  <button 
                    onClick={() => setIsCalendarOpen(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 hover:scale-[1.02] hover:shadow-md hover:opacity-90 active:scale-[0.98]"
                    style={{ backgroundColor: ctaBtnBg, color: ctaBtnText }}
                  >
                    Book an AI Demo →
                  </button>
                </div>
              </div>

              {/* Right Visual - AI Chat Mockup */}
              <AnimatedAIMockup />
            </div>

            {/* AI Feature Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-16">
              {[
                { icon: Phone, title: "Voice AI", description: "24/7 virtual sales agent that handles FAQs, qualifies leads, and books appointments with a human-like voice." },
                { icon: MessageSquare, title: "Conversation AI", description: "Instant chatbot replies across SMS, Web Chat, and Social DMs, pushing qualified leads directly to your calendar." },
                { icon: Star, title: "Reviews AI", description: "Automated reputation manager that drafts professional, on-brand replies to all reviews instantly." },
                { icon: FileText, title: "Content AI", description: "Generate high-quality copy for emails, blog outlines, and social media posts in seconds." },
                { icon: TrendingUp, title: "Funnel AI", description: "AI generates complete page structure, optimized layout, and persuasive copy by describing your offer." },
                { icon: GitBranch, title: "Workflow AI", description: "Describe your desired outcome and AI generates the entire automation workflow instantly." },
              ].map((feature, index) => {
                const gridLineColor = theme === 'dark' ? '#444444' : '#d0d0d0';
                const iconBoxStyle: React.CSSProperties = {
                  backgroundImage: `linear-gradient(to right, ${gridLineColor} 1px, transparent 1px), linear-gradient(to bottom, ${gridLineColor} 1px, transparent 1px)`,
                  backgroundSize: '5px 5px',
                  WebkitMaskImage: 'radial-gradient(circle, black 0%, transparent 70%)',
                  maskImage: 'radial-gradient(circle, black 0%, transparent 70%)',
                };
                return (
                  <div 
                    key={index}
                    className="rounded-xl p-5 transition-all duration-300 hover:shadow-lg hover:scale-[1.02] group"
                    style={{ 
                      backgroundColor: featureCardBg,
                      border: `1px solid ${borderColor}`
                    }}
                  >
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                      style={iconBoxStyle}
                    >
                      <feature.icon className="w-5 h-5" style={{ color: theme === 'dark' ? '#888888' : '#666666' }} strokeWidth={1.5} />
                    </div>
                    <h3 className="text-sm font-semibold mb-2" style={{ color: headingColor }}>
                      {feature.title}
                    </h3>
                    <p className="text-xs leading-relaxed" style={{ color: textColor }}>
                      {feature.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
        </GridLinesWrapper>

        <EditorialSectionDivider />

        {/* Done-for-you Marketing Section - KEEP VIDEO */}
        <GridLinesWrapper>
          <section className="py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <AnimatedBorderBadge text="Services" icon={Target} />

              <h2 className="text-3xl lg:text-4xl font-bold mb-4" style={{ color: headingColor }}>
                "Done-For-You" Marketing.
              </h2>
              
              <p className="text-sm max-w-2xl mx-auto mb-8" style={{ color: textColor }}>
                Let our experts handle your marketing while you focus on growing your business.
              </p>

              {/* Keep the video */}
              <div className="w-full max-w-2xl mx-auto mb-8">
                {/* Mobile video */}
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="md:hidden w-full h-auto rounded-xl transition-transform duration-300 hover:scale-[1.02]"
                  style={{ border: `1px solid ${borderColor}` }}
                >
                  <source 
                    src="https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/Story%20CRM%20-%20Reel.mp4" 
                    type="video/mp4" 
                  />
                </video>
                {/* Desktop video */}
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="hidden md:block w-full h-auto rounded-xl transition-transform duration-300 hover:scale-[1.02]"
                  style={{ border: `1px solid ${borderColor}` }}
                >
                  <source 
                    src="https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/FINAL-revised.mp4" 
                    type="video/mp4" 
                  />
                </video>
              </div>

              <button 
                onClick={() => navigate('/done-for-you')}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 hover:scale-[1.02] hover:shadow-md hover:opacity-90 active:scale-[0.98] mb-12"
                style={{ backgroundColor: ctaBtnBg, color: ctaBtnText }}
              >
                We Grow Businesses →
              </button>
            </div>

            {/* Service Boxes */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: Target, title: "Paid Ads", description: "Strategy that cuts through the noise with creative that demands attention and delivers real results." },
                { icon: Mail, title: "Email", description: "Full-funnel email strategy, data enrichment, and automation that maximizes conversion and retention." },
                { icon: Sparkles, title: "Creative", description: "High-impact creative that breaks through and scales across every format to drive your growth." },
                { icon: Monitor, title: "Web", description: "Websites that are conversion machines, driving better results through seamless user experiences." },
              ].map((service, index) => {
                const gridLineColor = theme === 'dark' ? '#444444' : '#d0d0d0';
                const iconBoxStyle: React.CSSProperties = {
                  backgroundImage: `linear-gradient(to right, ${gridLineColor} 1px, transparent 1px), linear-gradient(to bottom, ${gridLineColor} 1px, transparent 1px)`,
                  backgroundSize: '5px 5px',
                  WebkitMaskImage: 'radial-gradient(circle, black 0%, transparent 70%)',
                  maskImage: 'radial-gradient(circle, black 0%, transparent 70%)',
                };
                return (
                  <div 
                    key={index}
                    className="rounded-xl p-5 transition-all duration-300 hover:shadow-lg hover:scale-[1.02] group"
                    style={{ 
                      backgroundColor: cardBgColor,
                      border: `1px solid ${borderColor}`
                    }}
                  >
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                      style={iconBoxStyle}
                    >
                      <service.icon className="w-5 h-5" style={{ color: theme === 'dark' ? '#888888' : '#666666' }} strokeWidth={1.5} />
                    </div>
                    <h3 className="text-sm font-semibold mb-2" style={{ color: headingColor }}>
                      {service.title}
                    </h3>
                    <p className="text-xs leading-relaxed" style={{ color: textColor }}>
                      {service.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
        </GridLinesWrapper>

        <EditorialSectionDivider />

        {/* FAQ Section */}
        <GridLinesWrapper>
          <section className="py-16">
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-12">
              <AnimatedBorderBadge text="Answers" icon={HelpCircle} />

              <h2 className="text-3xl lg:text-4xl font-bold" style={{ color: headingColor }}>
                Frequently Asked Questions.
              </h2>
              <p className="mt-2 text-sm" style={{ color: textColor }}>
                Everything you need to know about our pricing and plans.
              </p>
            </div>

            <div className="space-y-2">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="overflow-hidden transition-all"
                  style={{ 
                    backgroundColor: cardBgColor,
                    border: `1px solid ${borderColor}`, 
                    borderRadius: '12px',
                  }}
                >
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left"
                  >
                    <h3 className="text-xs sm:text-sm font-semibold pr-4" style={{ color: headingColor }}>
                      {faq.question}
                    </h3>
                    <ChevronDown 
                      className={`w-4 h-4 sm:w-5 sm:h-5 shrink-0 transition-transform duration-300 ${
                        openFaqIndex === index ? 'rotate-180' : ''
                      }`}
                      style={{ color: headingColor }}
                    />
                  </button>
                  
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openFaqIndex === index ? 'max-h-96' : 'max-h-0'
                    }`}
                  >
                    <div className="px-6 pb-4">
                      <p className="text-xs sm:text-sm leading-relaxed" style={{ color: textColor }}>
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        </GridLinesWrapper>

        <EditorialSectionDivider />

        {/* CTA Section */}
        <GridLinesWrapper>
          <section className="py-16 px-6" style={{ backgroundColor: theme === 'dark' ? 'transparent' : '#FFFFFF' }}>
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
              {/* Left Content */}
              <div className="lg:flex-1">
                <AnimatedBorderBadge text="All-in-one" icon={Sparkles} align="left" />

                <h2 className="text-3xl lg:text-4xl font-bold mb-4 tracking-tight" style={{ color: headingColor }}>
                  Start Your 14 Day Free Trial.
                </h2>

                <p className="text-sm mb-6 max-w-2xl" style={{ color: textColor }}>
                  The first two weeks are on us. Stop the lead bleed today.
                </p>

                <Link 
                  to="/pricing#pricing-tables"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 hover:scale-[1.02] hover:shadow-md active:scale-[0.98] hover:opacity-90"
                  style={{ backgroundColor: ctaBtnBg, color: ctaBtnText }}
                >
                  Start for free →
                </Link>
              </div>

              {/* Right Stat Cards */}
              <div className="flex flex-row gap-3 lg:mt-6">
                <div 
                  className="rounded-xl p-4 w-32 transition-all duration-200 hover:scale-105 cursor-pointer"
                  style={{ 
                    backgroundColor: cardBgColor,
                    border: `1px solid ${borderColor}`,
                    boxShadow: theme === 'dark' 
                      ? '0 15px 30px -8px rgba(0, 0, 0, 0.4), 0 8px 16px -6px rgba(0, 0, 0, 0.3)'
                      : '0 15px 30px -8px rgba(0, 0, 0, 0.12), 0 8px 16px -6px rgba(0, 0, 0, 0.08)'
                  }}
                >
                  <div className="text-xl lg:text-2xl font-bold mb-0.5" style={{ color: headingColor }}>Free</div>
                  <div className="text-xs" style={{ color: textColor }}>CRM migration</div>
                </div>

                <div 
                  className="rounded-xl p-4 w-32 transition-all duration-200 hover:scale-105 cursor-pointer"
                  style={{ 
                    backgroundColor: cardBgColor,
                    border: `1px solid ${borderColor}`,
                    boxShadow: theme === 'dark' 
                      ? '0 15px 30px -8px rgba(0, 0, 0, 0.4), 0 8px 16px -6px rgba(0, 0, 0, 0.3)'
                      : '0 15px 30px -8px rgba(0, 0, 0, 0.12), 0 8px 16px -6px rgba(0, 0, 0, 0.08)'
                  }}
                >
                  <div className="text-xl lg:text-2xl font-bold mb-0.5" style={{ color: headingColor }}>1:1</div>
                  <div className="text-xs" style={{ color: textColor }}>onboarding</div>
                </div>

                <div 
                  className="rounded-xl p-4 w-32 transition-all duration-200 hover:scale-105 cursor-pointer"
                  style={{ 
                    backgroundColor: cardBgColor,
                    border: `1px solid ${borderColor}`,
                    boxShadow: theme === 'dark' 
                      ? '0 15px 30px -8px rgba(0, 0, 0, 0.4), 0 8px 16px -6px rgba(0, 0, 0, 0.3)'
                      : '0 15px 30px -8px rgba(0, 0, 0, 0.12), 0 8px 16px -6px rgba(0, 0, 0, 0.08)'
                  }}
                >
                  <div className="text-xl lg:text-2xl font-bold mb-0.5" style={{ color: headingColor }}>10</div>
                  <div className="text-xs" style={{ color: textColor }}>weeks training</div>
                </div>
              </div>
            </div>
          </div>
        </section>
        </GridLinesWrapper>

        <EditorialSectionDivider />

        {/* Footer */}
        <EditorialFooter />
        </div>
      </EditorialGridLayout>
      
      <CalendarPopup 
        isOpen={isCalendarOpen} 
        onClose={() => setIsCalendarOpen(false)} 
      />

      {/* Video Modal */}
      {showVideoModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setShowVideoModal(false)}
        >
          <div 
            className="relative w-full max-w-4xl mx-4 bg-black rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowVideoModal(false)}
              className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Video */}
            <video
              autoPlay
              controls
              className="w-full aspect-video"
              src="https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/Explainer%20V6%20-%20Complete%20Overview%20Explainer.mp4"
            />
          </div>
        </div>
      )}
    </>
  );
};

const Pricing = () => {
  usePageSEO({
    title: 'Marketing Platform Pricing | Plans Starting at $197/mo',
    description: 'Simple, transparent pricing for all-in-one marketing automation. Choose from SaaS or Done-For-You services. 14-day free trial, no setup fees.',
    canonical: 'https://storily.io/pricing',
    keywords: 'marketing automation pricing, CRM pricing, marketing platform cost, done for you marketing'
  });

  return <PricingContent />;
};

export default Pricing;
